import { useState } from 'react';
import { Button } from '../../../modules/design-system';
import type { ApiClient, IframePoolOrderItem, IframePoolShipping } from '../../../modules/api';
import {
  HighValueDisclaimer,
  ProtectionOptOutWarning,
  quotaOverflowMessage,
  quotaRemainingHint,
} from '../../../modules/shipping';
import { PromoBanner } from '../../../modules/shippingPromo';
import {
  DeliveryCard,
  DeliveryPageShell,
  SummaryDivider,
  SummaryRow,
} from '../shared';
import { AddressModal } from './AddressModal';
import { AddressPickerModal } from './AddressPickerModal';
import { ItemPickerModal } from './ItemPickerModal';
import { useShipmentDraft } from './useShipmentDraft';
import { YourAddressSection } from './sections/YourAddressSection';
import { ShippingOptionsSection } from './sections/ShippingOptionsSection';
import { ShippingListSection } from './sections/ShippingListSection';
import { QuotaFullNotice } from './sections/QuotaFullNotice';

export interface DeliveryNewPageProps {
  client: ApiClient;
  /** Initial pool_items, seeded from the My Packs Cards-tab selection. The
   *  player edits the set in-page via the item picker after that. */
  selectedItems: IframePoolOrderItem[];
  /** Top-bar "< Home" → cancel + return to My Packs. */
  onBack: () => void;
  /** Deprecated — item selection is now an in-page modal; still accepted so
   *  the parent can keep passing it harmlessly. */
  onChooseItems?: () => void;
  /** Fired after `createPoolShipping` succeeds; parent decides whether to
   *  flip to the Deliveries tab, open the new shipping's Show page, etc. */
  onSuccess: (shipping: IframePoolShipping) => void;
  /** Drop the noise-SVG overlay from the page gradient. */
  hideBackgroundPattern?: boolean;
}

/**
 * Delivery New page (Figma 710:3939). Address + Shipping options +
 * Shipping list + Summary + Submit. Address creation goes through
 * `AddressModal`. Services come from `getNewShipping`. Submit posts to
 * `createPoolShipping` and hands the new shipping back via `onSuccess`.
 */
export function DeliveryNewPage({
  client,
  selectedItems,
  onBack,
  onSuccess,
  hideBackgroundPattern = false,
}: DeliveryNewPageProps) {
  // Items in the shipment — seeded from the Cards-tab selection, then edited
  // in-page via the item picker (no navigating away).
  const [items, setItems] = useState<IframePoolOrderItem[]>(selectedItems);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const draft = useShipmentDraft(client, items, { preselectFirstService: true, onCreated: onSuccess });
  const {
    addresses,
    selected: address,
    loading: addressesLoading,
    select: selectAddress,
    addCreated,
  } = draft.addresses;
  const { fees, loading: feesLoading, error: feesError, retry: retryFees } = draft.fees;
  const { quota, quotaFull, quotaOverflow, isOptOut, optOutAcknowledged, setOptOutAcknowledged } = draft;
  const { shippingRowValue, showInsuranceRow, insuranceRowValue, handlingFee, salesTax, total, promo } =
    draft.summary;

  // Queue full → the form has nothing to offer; show the notice instead of a
  // page whose every control is a dead end.
  if (quota != null && quotaFull) {
    return (
      <DeliveryPageShell
        client={client}
        title="Delivery Information"
        onBack={onBack}
        hideBackgroundPattern={hideBackgroundPattern}
      >
        <DeliveryCard>
          <QuotaFullNotice quota={quota} onBack={onBack} />
        </DeliveryCard>
      </DeliveryPageShell>
    );
  }

  return (
    <DeliveryPageShell
      client={client}
      title="Delivery Information"
      onBack={onBack}
      hideBackgroundPattern={hideBackgroundPattern}
    >
      <DeliveryCard>
        <div className="flex flex-col gap-6">
          <YourAddressSection
            address={address}
            loading={addressesLoading}
            onClickAdd={() => setAddressModalOpen(true)}
            onChange={() => setPickerOpen(true)}
          />
          <ShippingOptionsSection
            services={draft.services}
            selectedId={draft.selectedServiceId}
            onSelect={draft.selectService}
            hasItems={items.length > 0}
            hasAddress={address !== null}
            optionPrices={fees?.optionPrices ?? {}}
            originalOptionPrices={fees?.promo?.originalOptionPrices ?? {}}
            loading={feesLoading}
            requiredCodeName={draft.requiredCodeName}
            onChooseItems={() => setItemPickerOpen(true)}
            onChooseAddress={() =>
              addresses.length > 0
                ? setPickerOpen(true)
                : setAddressModalOpen(true)
            }
          />

          {/* A single card over the insurable cap → informational only. */}
          {draft.showHighValueDisclaimer && <HighValueDisclaimer />}

          {/* Player removed a required protection → warn + require an explicit
              acknowledgement (gates Submit below). */}
          {isOptOut && (
            <ProtectionOptOutWarning
              acknowledged={optOutAcknowledged}
              onAcknowledgedChange={setOptOutAcknowledged}
            />
          )}

          {/* Fulfilment-queue cap: a plain hint while the basket still fits,
              an alert once it doesn't (Submit is blocked in that case). */}
          {quota != null && (quotaOverflow || quota.inProcessing > 0) && (
            <p
              role={quotaOverflow ? 'alert' : undefined}
              className="text-[12px] leading-[17px]"
              style={{
                color: quotaOverflow
                  ? 'var(--co-DeliveryNew-quotaOverflowColor, #F87171)'
                  : 'var(--co-DeliveryNew-quotaHintColor, rgba(255,255,255,0.55))',
              }}
            >
              {quotaOverflow
                ? quotaOverflowMessage(quota, items.length)
                : quotaRemainingHint(quota)}
            </p>
          )}

          <ShippingListSection
            items={items}
            onChooseItems={() => setItemPickerOpen(true)}
          />
        </div>

        <SummaryDivider />

        <div className="flex flex-col gap-3">
          {/* A promotion lowered these prices: say so above them, so the struck-through
              regular figures never read as unexplained. */}
          {promo?.showBanner && (
            <PromoBanner label={promo.label} discount={promo.discount} loading={feesLoading} />
          )}
          <SummaryRow
            label="Shipping"
            value={shippingRowValue}
            original={promo?.shipping}
            loading={feesLoading}
          />
          {showInsuranceRow && (
            <SummaryRow
              label="Insurance"
              value={insuranceRowValue}
              original={promo?.insurance}
              loading={feesLoading}
            />
          )}
          <SummaryRow
            label="Handling"
            value={handlingFee}
            original={promo?.handling}
            loading={feesLoading}
          />
          {salesTax !== null && (
            <SummaryRow label="Sales tax" value={salesTax} loading={feesLoading} />
          )}
          <SummaryRow
            label="Total"
            value={total}
            original={promo?.total}
            emphasis
            loading={feesLoading}
          />
        </div>

        {/* Live shipping quote failed → tell the player what to do instead of
            silently showing "—": a transient upstream failure offers a retry;
            an unrateable address prompts a fix (retrying it won't help). */}
        {feesError && (
          <div
            role="alert"
            className="mt-3 flex flex-col items-start gap-2 text-[12px]"
            style={{ color: '#F87171' }}
          >
            <p>
              {feesError === 'unrateable_address'
                ? "We couldn't calculate shipping for this address. Please check the address and try again."
                : "We couldn't calculate shipping right now. Please try again in a moment."}
            </p>
            {feesError !== 'unrateable_address' && (
              <Button variant="glass-frosted" size="sm" onClick={retryFees}>
                Try again
              </Button>
            )}
          </div>
        )}

        {draft.error && (
          <p role="alert" className="mt-3 text-[12px]" style={{ color: '#F87171' }}>
            {draft.error}
          </p>
        )}

        <div className="mt-5">
          <Button
            variant={draft.canSubmit ? 'primary' : 'glass-frosted'}
            size="lg"
            onClick={() => void draft.submit()}
            disabled={!draft.canSubmit}
            loading={draft.submitLoading}
            fullWidth
          >
            Submit
          </Button>
        </div>
      </DeliveryCard>

      {pickerOpen && (
        <AddressPickerModal
          addresses={addresses}
          selectedId={address?.id ?? null}
          onSelect={(addr) => {
            selectAddress(addr);
            setPickerOpen(false);
          }}
          onAddNew={() => {
            setPickerOpen(false);
            setAddressModalOpen(true);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {addressModalOpen && (
        <AddressModal
          client={client}
          onClose={() => setAddressModalOpen(false)}
          onSuccess={(addr) => {
            addCreated(addr);
            setAddressModalOpen(false);
            setPickerOpen(false);
          }}
        />
      )}

      {itemPickerOpen && (
        <ItemPickerModal
          client={client}
          selectedItems={items}
          maxSelectable={quota?.remaining ?? null}
          onApply={(picked) => {
            setItems(picked);
            setItemPickerOpen(false);
          }}
          onClose={() => setItemPickerOpen(false)}
        />
      )}
    </DeliveryPageShell>
  );
}
